import React from "react";
import { Button, CircularProgress, ButtonProps } from "@mui/material";

interface LoadingButtonProps extends ButtonProps {
  loading?: boolean;
  loadingPosition?: "start" | "end" | "center";
  loadingIndicator?: React.ReactNode;
}

/**
 * Custom LoadingButton component to replace @mui/lab/LoadingButton
 * Displays a loading spinner when the loading prop is true
 */
const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading = false,
  loadingPosition = "center",
  loadingIndicator,
  children,
  disabled,
  startIcon,
  endIcon,
  ...other
}) => {
  const defaultLoadingIndicator = (
    <CircularProgress
      size={20}
      sx={{
        color: "inherit",
      }}
    />
  );

  const spinner = loadingIndicator || defaultLoadingIndicator;

  const getContent = () => {
    if (!loading) {
      return children;
    }

    if (loadingPosition === "start") {
      return (
        <>
          {spinner}
          <span style={{ marginLeft: 8 }}>{children}</span>
        </>
      );
    }

    if (loadingPosition === "end") {
      return (
        <>
          {children}
          <span style={{ marginLeft: 8 }}>{spinner}</span>
        </>
      );
    }

    // center position
    return spinner;
  };

  return (
    <Button
      disabled={disabled || loading}
      startIcon={loading && loadingPosition === "start" ? undefined : startIcon}
      endIcon={loading && loadingPosition === "end" ? undefined : endIcon}
      {...other}
    >
      {getContent()}
    </Button>
  );
};

export default LoadingButton;
